"use client";

import clsx from "clsx";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { AcfText, SectionProps } from "@/components/page-builder/types";
import WpImage from "@/components/ui/WpImage";
import type { MaybeMedia } from "@/lib/media";
import { filled, list, richText } from "./text";

// The chevron points up; the buttons turn it. Both icons are white SVGs painted as backgrounds.
const CHEVRON =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M18 15l-6-6-6 6'/%3E%3C/svg%3E\")";
const CROSS =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M18 6L6 18M6 6l12 12'/%3E%3C/svg%3E\")";

const ICONS = {
  "--das-stages-chevron": CHEVRON,
  "--das-stages-cross": CROSS,
} as CSSProperties;

// The media fills the section; desktop and mobile sources are swapped at the mobile breakpoint.
const FILL = "absolute top-0 left-0 size-full object-cover";

const NAV_BUTTON =
  "relative block size-8 p-0 border border-white/30 rounded-full bg-transparent cursor-pointer appearance-none transition-opacity duration-400 ease-[ease] " +
  "before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:size-[15px] before:-mt-[7px] before:-ml-[7px] before:bg-(image:--das-stages-chevron) before:bg-no-repeat before:bg-center before:bg-contain";

type Stage = {
  title?: AcfText;
  text?: AcfText;
  /** The [COMP] Background fields. */
  media?:
    | {
        image?: MaybeMedia;
        image_mobile?: MaybeMedia;
        video?: MaybeMedia;
        video_mobile?: MaybeMedia;
      }
    | false;
};

type Fields = {
  /** Plain text; *stars* highlight a phrase, new lines break. */
  title?: AcfText;
  text?: AcfText;
  stages?: Stage[] | false;
};

/**
 * DAS Academies Stages, with the port of das-stages.js.
 *
 * Full-bleed section whose background belongs to the selected stage. Two
 * states: nothing selected, where the pills sit as a plain list over the first
 * stage's media, and a stage selected, where that pill becomes a card and the
 * close control appears. The prev / next nav is always on screen.
 *
 * Every stage's media is in the markup from the start and swapped by data-active, so
 * changing stage never waits on a download. Videos are lazy: the source lives
 * in data-src so six of them do not all download at once. The one being shown
 * is promoted on demand and the rest are paused.
 */
export default function Stages({
  title,
  text,
  stages,
  section_id,
}: SectionProps<Fields>) {
  const rows = list(stages);
  const sectionRef = useRef<HTMLElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);

  // Index of the selected stage, or null at rest.
  const [active, setActive] = useState<number | null>(null);

  // Index of the layer on show; the first stage's until the section is seen.
  const [shown, setShown] = useState(0);

  // The resting media only starts once the section is actually on screen.
  const [seen, setSeen] = useState(false);

  // Stages without a title have no pill, but keep their index (data-stage).
  const items = rows.flatMap((stage, index) =>
    filled(stage.title) ? [index] : [],
  );

  // Indices of the stages that have a layer; one with no media has none, and
  // selecting it leaves the previous layer on show, as the theme did.
  const layers = rows.flatMap((stage, index) =>
    stage.media && (stage.media.image || stage.media.video) ? [index] : [],
  );

  /**
   * Show the media for a stage, falling back to the first one when nothing is
   * selected — that is what the section rests on.
   */
  const showLayer = (index: number | null) => {
    const target = index === null ? 0 : index;

    if (layers.includes(target)) {
      setShown(target);
    }
  };

  /** Select a stage, or pass null to collapse back to the resting state. */
  const select = (index: number | null) => {
    setActive(index);
    setSeen(true);
    showLayer(index);
  };

  /**
   * Step through the stages, collapsing once you walk off the end — the design
   * has no wrap-around. From the resting state next opens the first stage and
   * prev has nowhere to go.
   *
   * Like the theme, this walks positions in the list of pills, which are the
   * stage indices themselves unless a stage was left without a title.
   */
  const move = (step: number) => {
    if (active === null) {
      if (step > 0) {
        select(0);
      }

      return;
    }

    const next = active + step;

    select(next < 0 || next >= items.length ? null : next);
  };

  useEffect(() => {
    const section = sectionRef.current;

    if (!section) {
      return;
    }

    if (!("IntersectionObserver" in window)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSeen(true);

      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          setSeen(true);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.25 },
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, []);

  // Start the videos in the layer on show and pause every other layer's.
  useEffect(() => {
    const media = mediaRef.current;

    if (!media || !seen) {
      return;
    }

    media
      .querySelectorAll<HTMLElement>(":scope > [data-stage]")
      .forEach((layer) => {
        const isShown = layer.getAttribute("data-stage") === String(shown);

        layer.querySelectorAll("video").forEach((video) => {
          if (!isShown) {
            video.pause();

            return;
          }

          const src = video.getAttribute("data-src");

          if (src && video.getAttribute("src") !== src) {
            video.setAttribute("src", src);
            video.load();
          }

          video.play()?.catch(() => {});
        });
      });
  }, [seen, shown]);

  if (!rows.length) {
    return null;
  }

  const open = active !== null;

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden min-h-[88svh] bg-das-black [--card-bg:rgba(44,44,46,0.92)] mobile:min-h-[92svh] js-das-stages"
      style={ICONS}
      data-id={section_id || undefined}
      data-open={open}
    >
      <div ref={mediaRef} className="absolute top-0 left-0 size-full z-1">
        {rows.map((stage, index) => {
          const media = stage.media || {};
          const image = media.image || false;
          const video = media.video || false;

          if (!image && !video) {
            return null;
          }

          const imageMobile = media.image_mobile || image;
          const videoMobile = media.video_mobile || video;

          return (
            <div
              key={index}
              className="absolute top-0 left-0 size-full opacity-0 invisible transition-[opacity,visibility] duration-500 ease-[ease] data-[active=true]:opacity-100 data-[active=true]:visible"
              data-stage={index}
              data-active={index === shown}
            >
              {video ? (
                <>
                  <video
                    className={clsx(FILL, "mobile:hidden")}
                    muted
                    loop
                    playsInline
                    data-src={video.src}
                  ></video>
                  <video
                    className={clsx(FILL, "hidden mobile:block")}
                    muted
                    loop
                    playsInline
                    data-src={(videoMobile || video).src}
                  ></video>
                </>
              ) : (
                <>
                  <WpImage
                    media={image}
                    className={clsx(FILL, "mobile:hidden")}
                    alt=""
                  />
                  <WpImage
                    media={imageMobile}
                    className={clsx(FILL, "hidden mobile:block")}
                    alt=""
                  />
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="absolute top-0 left-0 size-full z-2 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.55)_0%,rgba(0,0,0,0)_35%,rgba(0,0,0,0)_55%,rgba(0,0,0,0.8)_100%)]"></div>

      {/* Padding, not margin: the height has to hold wherever the section is placed. */}
      <div className="relative z-3 min-h-[88svh] flex pt-14 pb-12 mobile:min-h-[92svh] mobile:pt-10 mobile:pb-8">
        <div className="shell shell-s flex">
          <div className="w-full flex flex-col justify-between">
            {(filled(title) || filled(text)) && (
              <div className="max-w-[620px] js-das-reveal">
                {filled(title) && (
                  <h2 className="mb-0! font-das text-[38px] leading-[1.08] font-bold tracking-[-0.02em] text-white mobile:text-[28px]">
                    {richText(String(title), "text-das-green")}
                  </h2>
                )}

                {filled(text) && (
                  <p className="max-w-[36ch] mt-3 font-das text-[15px] leading-[1.55] font-normal text-das-green mobile:text-[14px]">
                    {richText(String(text), "text-das-green")}
                  </p>
                )}
              </div>
            )}

            <div className="flex items-end mt-10 mobile:block mobile:mt-8 mobile:mx-[calc(50%-50vw)] mobile:px-3.75">
              {/*
               * The nav is part of the furniture rather than something the open
               * state brings in, so it stays put; prev simply has nothing to
               * step back to until a stage is open. The list runs down the
               * column on desktop and across it on mobile, so the chevrons turn
               * with it.
               */}
              <div className="w-8 mr-3 mobile:flex mobile:items-center mobile:w-auto mobile:mr-0 mobile:mb-3">
                <button
                  type="button"
                  className={clsx(
                    NAV_BUTTON,
                    "mobile:before:-rotate-90",
                    open ? "hover:opacity-70" : "opacity-35 cursor-default",
                  )}
                  aria-label="Previous stage"
                  onClick={() => move(-1)}
                ></button>
                <button
                  type="button"
                  className={clsx(
                    NAV_BUTTON,
                    "hover:opacity-70 mt-1 before:rotate-180 mobile:mt-0 mobile:ml-2 mobile:before:rotate-90",
                  )}
                  aria-label="Next stage"
                  onClick={() => move(1)}
                ></button>
              </div>

              <ul className="m-0 p-0 list-none mobile:flex mobile:items-end mobile:overflow-x-auto mobile:[scrollbar-width:none] mobile:[-ms-overflow-style:none] mobile:[&::-webkit-scrollbar]:hidden">
                {items.map((index) => {
                  const stage = rows[index];
                  const isActive = index === active;

                  return (
                    <li
                      key={index}
                      className={clsx(
                        "m-0 [&+&]:mt-2 mobile:shrink-0 mobile:[&+&]:mt-0 mobile:[&+&]:ml-3",
                        // On mobile the list scrolls sideways, so once a stage opens the other pills go rather than being left to scroll past.
                        isActive
                          ? "mobile:block mobile:w-full mobile:max-w-full"
                          : clsx(
                              "mobile:max-w-[84vw]",
                              open && "mobile:hidden",
                            ),
                      )}
                      data-stage={index}
                      data-active={isActive}
                    >
                      {/* The pill and its expanded card share the item, so the column does not jump as you step through. */}
                      <button
                        type="button"
                        className={clsx(
                          "items-center py-3 pr-5 pl-3 border-0 rounded-[100px] bg-(--card-bg) backdrop-blur-[16px] text-left cursor-pointer appearance-none transition-[filter] duration-400 ease-[ease] hover:brightness-110",
                          isActive ? "hidden" : "flex",
                        )}
                        onClick={() => select(index)}
                      >
                        <span className="flex items-center justify-center size-8 mr-3 border border-white/40 rounded-full font-das text-[17px] leading-none text-white">
                          +
                        </span>
                        <span className="font-das text-[15px] leading-[1.2] font-semibold text-white">
                          {stage.title}
                        </span>
                      </button>

                      <div
                        className={clsx(
                          "max-w-[340px] py-5 px-6 rounded-[20px] bg-(--card-bg) backdrop-blur-[16px] mobile:max-w-full",
                          isActive ? "block" : "hidden",
                        )}
                      >
                        <p className="font-das text-[15px] leading-[1.65] font-normal text-white/80">
                          <span className="font-bold text-white">
                            {stage.title}.
                          </span>
                          {filled(stage.text) && (
                            <>
                              {" "}
                              <span>{stage.text}</span>
                            </>
                          )}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        className={clsx(
          "absolute top-10 right-10 z-4 size-9 p-0 border border-white/30 rounded-full bg-(--card-bg) backdrop-blur-[4px] cursor-pointer appearance-none transition-[opacity,visibility] duration-400 ease-[ease] mobile:top-5 mobile:right-5",
          "before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:size-4 before:-mt-2 before:-ml-2 before:bg-(image:--das-stages-cross) before:bg-no-repeat before:bg-center before:bg-contain",
          open ? "opacity-100 visible" : "opacity-0 invisible",
        )}
        aria-label="Close"
        onClick={() => select(null)}
      ></button>
    </section>
  );
}
